import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { useWriterComponentState } from "hooks";

import { MarkShipmentAsPickedUpModalContent } from "./MarkShipmentAsPickedUpModalContent";
import { ClientAppEvent } from "../../constants";

interface Props {
  onModalClose: VoidFunction;
  modalOpen: boolean;
  shipmentId: number;
  onMarkPickedUpSuccess?: VoidFunction | null;
}

export function MarkShipmentAsPickedUpModal(props: Props) {
  const {
    shipmentId,
    modalOpen,
    onModalClose,
    onMarkPickedUpSuccess = null,
  } = props;

  const posthog = usePostHog();
  const [notes, setNotes] = useState<string>("");
  const [deliveryTimestamp, setDeliveryTimestamp] = useState<string>(
    DateTime.now().toFormat("yyyy-MM-dd'T'T"),
  );
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const { loading, write, error } = useWriterComponentState({
    errorMessage:
      "Failed to mark as picked up. Please contact us if the issue persists.",
    writeConfig: {
      method: "post",
      url: `/shipments/${shipmentId}/mark-picked-up`,
      body: {
        notes,
        pickupTimestamp: DateTime.fromISO(deliveryTimestamp).toUTC().toISO(),
      },
    },
  });

  const submit = () => {
    const pickupAsDateTime = DateTime.fromISO(deliveryTimestamp).toUTC();
    if (pickupAsDateTime > DateTime.now().toUTC()) {
      setSubmissionError("Pickup timestamp cannot be in the future.");
      return;
    }

    // validation cleared
    setSubmissionError(null);
    write(() => {
      posthog?.capture(ClientAppEvent.SHIPMENT_MARKED_AS_PICKED_UP, {
        shipment_id: shipmentId,
        has_notes: notes.length > 0,
      });
      if (onMarkPickedUpSuccess) {
        onMarkPickedUpSuccess();
      }
      onModalClose();
    });
  };

  return (
    <Dialog fullWidth open={modalOpen} onClose={onModalClose}>
      <DialogTitle component="div">
        <Typography variant="h4">Mark shipment as picked up</Typography>
      </DialogTitle>
      <DialogContent>
        <MarkShipmentAsPickedUpModalContent
          notes={notes}
          onNotesUpdate={(newValue: string) => setNotes(newValue)}
          loading={loading}
          error={error}
          pickupTimestamp={deliveryTimestamp}
          onPickupTimestampUpdate={(newValue: string) =>
            setDeliveryTimestamp(newValue)
          }
        />
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Button autoFocus variant="outlined" onClick={onModalClose}>
            Go Back
          </Button>
          <Button onClick={submit} variant="contained">
            {!error ? "Mark Picked Up" : "retry"}
          </Button>
        </DialogActions>
      )}
      <ErrorTypography error={submissionError} />
    </Dialog>
  );
}
